// THE STATS TAB (founder 2026-11-04, "Yes to all 6, build them"): how
// members answered a Daily Question, evening by evening, on the question's
// View page. Admin had no Daily Question numbers at all. The backend
// (GET /admin/vaultQuestionStats/:id, admin.vaultQuestionController
// statsFor) counts exactly like the asker's You tab (utils/dailyQuestionAsk
// mine): every answer in that city on that evening, test accounts included,
// one count per answer position the question has now. Counts only: no
// member is ever named beside an answer, and the asker's own pick is not
// shown. Pure, for the tests.
//
// The response, data:
//   applies   false for a tier other than dailyQuestion (nothing else here)
//   options   the answers, every position kept
//   dropHour  20; live: the live day "YYYY-MM-DD"
//   member    null for a bank question, else { firstName, declined,
//             declinedNeverShown }
//   runs      newest first: { city, reviewCity, dateKey, state ("tonight",
//             "open", "closed"), closesKey, answered, counts, leaders,
//             testAnswers, testAccounts, askerSees (true, false, or null on
//             a bank question), results ({ notice, at } or null) }
//   total     null, or { evenings, answered, counts, leaders, testAnswers,
//             testAccounts, open } over 2 or more evenings outside the
//             review city
//   moreEvenings  true when it ran on more evenings than the newest 60 the
//             server reads (STATS_RUNS_MAX): runs and total cover only those
// Every read below is tolerant of a missing field (an older server sends no
// testAccounts, declinedNeverShown or moreEvenings).

import { eveningLong, eveningShort, weekdayShort, hourWords, etDayKey } from "./memberQuestionUtils";

const ET_ZONE = "America/Toronto";
const num = (n) => (typeof n === "number" && Number.isFinite(n) ? n : 0);

/** Math.round(count / answered * 100), as the app's results sheet. */
export const pctOf = (count, answered) => (num(answered) > 0 ? Math.round((num(count) / num(answered)) * 100) : 0);

/** "A", "A and B", "A, B and C". */
export const joinAnd = (xs) => {
  const list = (xs || []).filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

/** An answer's words; a blank answer keeps its place. */
export const optionLabel = (options, i) => {
  const text = String((options || [])[i] ?? "").trim();
  return text || `Answer ${i + 1} (blank)`;
};

/** "Ottawa · Tuesday, Nov 3", with " · Review city" in the review city. */
export const runHeader = (run) =>
  `${(run && run.city) || "Unknown city"} · ${eveningLong(run && run.dateKey)}${run && run.reviewCity ? " · Review city" : ""}`;

/**
 * The pill beside the header: { text, open } for an evening shown, null for
 * one dealt and not shown yet. Answers close at the next evening's drop.
 */
export const runStatePill = (run, dropHour) => {
  if (!run || run.state === "tonight") return null;
  const hour = hourWords(dropHour);
  return run.state === "open"
    ? { text: `Answers open until ${hour} ET ${eveningShort(run.closesKey)}`, open: true }
    : { text: `Answers closed ${eveningShort(run.closesKey)}, ${hour} ET`, open: false };
};

/**
 * An evening dealt at 7 PM and not shown yet. withCity: the tab has nothing
 * else to show, so the line names the city.
 */
export const soonLine = (run, dropHour, { withCity = false, now = new Date() } = {}) => {
  const where = withCity && run && run.city ? ` in ${run.city}` : "";
  const hour = hourWords(dropHour);
  const when = run && run.dateKey === etDayKey(now) ? `tonight${where} at ${hour} ET` : `${where ? `${where.trim()} ` : ""}on ${eveningLong(run && run.dateKey)} at ${hour} ET`;
  return `Shows ${when}. The numbers appear here from then.`;
};

/** "Answered" or, while answers are still coming in, "Answered so far". */
export const answeredLabel = (open) => (open ? "Answered so far" : "Answered");

/** No answers: "No answers yet." while open, "Nobody answered." after. */
export const noAnswersLine = (open) => (open ? "No answers yet." : "Nobody answered.");

/**
 * The "Most chosen" tile: { value, sub }, or null with nobody answering.
 * One leader: its words, "15 of 34 · 44%". A tie: "Tied", "Always and
 * Absolutely not, 12 each".
 */
export const mostChosen = (block, options) => {
  const answered = num(block && block.answered);
  const leaders = (block && Array.isArray(block.leaders) ? block.leaders : []).filter((i) => Number.isInteger(i));
  if (answered <= 0 || !leaders.length) return null;
  const count = num((block.counts || [])[leaders[0]]);
  if (leaders.length === 1) {
    return { value: optionLabel(options, leaders[0]), sub: `${count} of ${answered} · ${pctOf(count, answered)}%` };
  }
  return { value: "Tied", sub: `${joinAnd(leaders.map((i) => optionLabel(options, i)))}, ${count} each` };
};

/** The bars, in the answers' own order: { label, count, pct, leader }. */
export const barsOf = (block, options) => {
  const answered = num(block && block.answered);
  const leaders = new Set(block && Array.isArray(block.leaders) ? block.leaders : []);
  return (options || []).map((_, i) => {
    const count = num(((block && block.counts) || [])[i]);
    return { label: optionLabel(options, i), count, pct: pctOf(count, answered), leader: answered > 0 && leaders.has(i) };
  });
};

/** The leader's mini pill: "Most chosen", or "Tied for most chosen". */
export const leaderPill = (block) =>
  (block && Array.isArray(block.leaders) ? block.leaders.length : 0) > 1 ? "Tied for most chosen" : "Most chosen";

/**
 * Test accounts in a count (the asker's You tab counts them too). One
 * evening: one answer per account, so "Includes 2 test accounts." Across
 * evenings an account can answer more than once, so the total counts
 * answers.
 */
export const testNote = (n, { total = false } = {}) => {
  const k = num(n);
  if (k <= 0) return "";
  if (total) return k === 1 ? "Includes 1 answer from a test account." : `Includes ${k} answers from test accounts.`;
  return k === 1 ? "Includes 1 test account." : `Includes ${k} test accounts.`;
};

/**
 * The total block's note, in the approved words "Includes N test accounts."
 * (review 2026-11-04): the server's total.testAccounts counts each test
 * account once over all the evenings (Jack in Ottawa and in Toronto is one).
 * An older server sends only testAnswers, so the answers' words then.
 */
export const totalTestNote = (total) =>
  total && typeof total.testAccounts === "number" ? testNote(total.testAccounts) : testNote(total && total.testAnswers, { total: true });

/** One evening's note: one answer per account there, so either count. */
export const runTestNote = (run) => testNote(run && typeof run.testAccounts === "number" ? run.testAccounts : run && run.testAnswers);

/**
 * Said when the question ran on more evenings than the server reads (review
 * 2026-11-04): without it "All cities · 60 evenings" would read as every
 * evening. n is the evenings listed.
 */
export const moreEveningsNote = (data, n) => {
  if (!(data && data.moreEvenings === true)) return "";
  const k = num(n);
  return `Only the newest ${k} ${k === 1 ? "evening is" : "evenings are"} listed and counted here. Older evenings are left out.`;
};

/** Said only when these are the numbers on the asker's You tab (mine reads their current city). */
export const askerSeesNote = (firstName) =>
  firstName ? `These are the numbers on ${firstName}'s You tab.` : "These are the numbers on the asker's You tab.";

const etTime = (at) => {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { timeZone: ET_ZONE, hour: "numeric", minute: "2-digit" });
};
const etDayTime = (at) => {
  const d = new Date(at);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-US", { timeZone: ET_ZONE, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/**
 * The 10 AM results push to the asker, for a member question's evening
 * (backend utils/dailyQuestionResults: the day row's resultsNotice and
 * resultsCheckedAt, the moment the morning sweep decided). "" on a bank
 * question and on an evening not shown yet.
 */
export const resultsLine = (run, { member, firstName } = {}) => {
  if (!member || !run || run.state === "tonight") return "";
  const who = firstName || "the asker";
  const Who = firstName || "The asker";
  // The morning sweep never reads the review city (review 2026-11-04:
  // dailyQuestionResults morningResults keeps isReviewPocket out), so no
  // decision is ever coming there.
  if (run.reviewCity === true) return "Results push: none in the review city.";
  const r = run.results;
  if (!r || !r.notice) {
    if (run.state === "open") return `Results push: decided from 10 AM ET ${weekdayShort(run.closesKey)}.`;
    return "Results push: none.";
  }
  const at = r.at ? ` at ${etTime(r.at)} ET` : "";
  switch (r.notice) {
    case "told":
      return r.at ? `Results push: sent to ${who} ${etDayTime(r.at)} ET.` : `Results push: sent to ${who}.`;
    case "ready":
      return `Results push: decided to send${at}, with no result recorded yet.`;
    // Written once, when the push rules defer the send; nothing updates the
    // row after (review 2026-11-04), so it never says whether it went.
    case "held":
      return `Results push: held by the push rules${at}. Whether it went out later is not recorded.`;
    case "no_answers":
      return `Results push: not sent. Nobody had answered${at}.`;
    // Rows decided before 2026-11-04, when the push needed
    // dailyQuestion.statThreshold answers. The number is not on the row,
    // and a push sent by hand is not either.
    case "under_threshold":
      return "Results push: not sent by the 10 AM check. Fewer had answered than the minimum it used then.";
    case "asker_moved":
      return `Results push: not sent. ${Who} had moved to another city.`;
    case "asker_on_break":
      return `Results push: not sent. ${Who} was on a break.`;
    case "member_gone":
      return `Results push: not sent. ${firstName ? `${firstName}'s` : "The asker's"} account was gone.`;
    case "member_not_active":
      return `Results push: not sent. ${firstName ? `${firstName}'s` : "The asker's"} account was not active.`;
    case "test_in_real_city":
      return "Results push: not sent. A test account's question is never pushed in a real city.";
    case "test_account_now":
      return `Results push: not sent. ${Who} was a test account in a real city.`;
    case "not_on_you_tab":
      return `Results push: not sent. It was not on ${firstName ? `${firstName}'s` : "the asker's"} You tab.`;
    case "review_city":
      return "Results push: none in the review city.";
    case "send_failed":
      return "Results push: the send failed.";
    default:
      return `Results push: not sent (${r.notice}).`;
  }
};

/**
 * The tab with no evening at all. "any evening": an inactive or declined
 * bank question may never be picked (review 2026-11-04), so the words do
 * not promise an evening. Declined reads the server's declinedNeverShown,
 * else declined (an older server; the same with no evening).
 */
export const emptyLine = (data, dropHour) => {
  const m = data && data.member;
  const declined = m && (typeof m.declinedNeverShown === "boolean" ? m.declinedNeverShown : m.declined);
  return declined
    ? "Declined, so it was never shown."
    : `Not shown yet. The numbers appear here from ${hourWords(dropHour)} ET on any evening it is shown.`;
};

export const STATS_INTRO = "How members answered, evening by evening. Counts only, never who chose what.";

/** The foot of the tab. */
export const statsFootnote = (dropHour) => {
  const hour = hourWords(dropHour);
  return `Answered counts each member in that city who tapped an answer between ${hour} ET that evening and ${hour} ET the next evening. An answer is final on tap, and there is no skip.`;
};

export const REVIEW_CITY_TOTAL_NOTE = "Review city evenings are listed but left out of this total.";

/** "All cities · 17 evenings". */
export const totalHeader = (total) => {
  const k = num(total && total.evenings);
  return `All cities · ${k} ${k === 1 ? "evening" : "evenings"}`;
};
